import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { get } from '../utils';
import { DeviceCatalogIndex, DeviceCatalogResponse } from 'src/types/DeviceTypeCapability';
import { indexByType, setDeviceCatalogCache } from 'src/utils/deviceCatalogCache';

enum QueryKeys {
  DeviceCatalog = 'DeviceCatalog',
}

export function getDeviceCatalog(): Promise<DeviceCatalogResponse> {
  return get<DeviceCatalogResponse>('v2/device-types');
}

/**
 * Device-type catalog (GET /v2/device-types) — the catalog-driven source of truth for device
 * capabilities, icon keys and display names. The result is mirrored into the module cache
 * (`deviceCatalogCache`) during render so the synchronous `getDeviceIcon` / `formatDeviceName` read the
 * fresh catalog in the SAME render react-query re-renders subscribers on load (an effect would update
 * it one commit too late and never re-render). The write is idempotent (same memoised index).
 * Returns the capability-by-type index for component use.
 */
export function useDeviceCatalog(): DeviceCatalogIndex {
  const { data } = useQuery<DeviceCatalogResponse>([QueryKeys.DeviceCatalog], getDeviceCatalog, {
    staleTime: 1000 * 60 * 60, // 1h — the catalog rarely changes within a session
  });

  const index = useMemo(() => (data ? indexByType(data) : {}), [data]);

  // Only populate the cache — never write {} — so the render-phase mirror is idempotent + monotonic and
  // safe to repeat/replay under Concurrent Mode (a discarded/interrupted render can't clear an already-
  // populated cache before commit). The cache keeps its last value until real data overwrites it.
  if (data) {
    setDeviceCatalogCache(index);
  }

  return index;
}

/**
 * Load state of the same catalog query, for callers that must tell "the catalog is empty" from "the
 * catalog could not be loaded". `useDeviceCatalog` deliberately returns `{}` in both cases so every
 * consumer can render something, which means an empty index alone cannot carry that distinction.
 *
 * Additive: shares the query key, so it costs no extra request.
 */
export function useDeviceCatalogStatus(): { isLoading: boolean; isError: boolean } {
  const { isLoading, isError } = useQuery<DeviceCatalogResponse>([QueryKeys.DeviceCatalog], getDeviceCatalog, {
    staleTime: 1000 * 60 * 60,
  });

  return { isLoading, isError };
}
